/**
 * Permission management utility
 * Centralized permission checking based on menu type and department
 * 
 * This replaces scattered username checks like:
 *   const isHuda = (user.id_pengguna || "").toLowerCase() === 'huda';
 * 
 * With centralized permission checks like:
 *   if (hasPermission(user, 'CROSS_DEPARTMENT_VIEW')) { ... }
 */

/**
 * Permission definitions
 * Each permission specifies which menu types and/or departments have access
 */
export const PERMISSIONS = {
  /**
   * View data across all departments
   * Used for users who need to see data from multiple departments
   */
  CROSS_DEPARTMENT_VIEW: {
    menu: ["ADMIN", "KAUNTER"],  // Admin and counter staff can see all departments
    bahagian: [],                 // Empty = no department restrictions
  },

  /**
   * Manage users and system settings
   * Full admin access to pengguna table and system configuration
   */
  MANAGE_USERS: {
    menu: ["ADMIN"],
    bahagian: [],
  },

  /**
   * View and manage notis tunggakan
   * Access to notice/reminder features
   */
  VIEW_NOTIS: {
    menu: ["BAHAGIAN", "ADMIN"],
    bahagian: ["UEU", "UUU", "HT"],  // Specific departments that use notis
  },

  /**
   * Generate and manage invoices
   * Access to inbois generation features
   */
  GENERATE_INBOIS: {
    menu: ["BAHAGIAN", "ADMIN"],
    bahagian: [],  // All departments can generate invoices
  },

  /**
   * Print reports and documents
   * Access to print functionality
   */
  PRINT_REPORTS: {
    menu: ["BAHAGIAN", "ADMIN", "KAUNTER"],
    bahagian: [],  // All users can print
  },

  /**
   * Edit customer/tenant data
   * Access to modify nama and akaun tables
   */
  EDIT_CUSTOMERS: {
    menu: ["BAHAGIAN", "ADMIN"],
    bahagian: [],  // Department staff can edit their own customers
  },

  /**
   * View admin panel and data tables
   * Access to /admin routes
   */
  VIEW_ADMIN_PANEL: {
    menu: ["ADMIN"],
    bahagian: [],
  },
};

/**
 * Check if user has a specific permission
 * 
 * @param {Object} user - User object with menu and bahagian properties
 * @param {string} permissionKey - Permission key from PERMISSIONS object
 * @returns {boolean} - True if user has permission
 * 
 * @example
 * if (hasPermission(user, 'CROSS_DEPARTMENT_VIEW')) {
 *   // Show data from all departments
 * } else {
 *   // Show only user's department data
 * }
 */
export const hasPermission = (user, permissionKey) => {
  if (!user || !PERMISSIONS[permissionKey]) {
    console.warn(`Permission check failed: user=${!!user}, permission=${permissionKey}`);
    return false;
  }

  const menu = (user.menu || "").trim().toUpperCase();
  const dept = (user.bahagian || "").trim().toUpperCase();
  const permission = PERMISSIONS[permissionKey];

  // Check menu type first (primary permission)
  if (permission.menu.includes(menu)) {
    return true;
  }

  // Check department (secondary permission)
  // Empty bahagian array means "all departments"
  if (permission.bahagian.length === 0) {
    return true;
  }

  // Check if user's department is in allowed list
  if (permission.bahagian.includes(dept)) {
    return true;
  }

  return false;
};

/**
 * Get default landing page route for user
 * Determines where user should be redirected after login
 * 
 * @param {Object} user - User object with menu, bahagian, id_pengguna
 * @returns {string} - Route path
 * 
 * @example
 * const route = getDefaultRoute(user);
 * navigate(route);
 */
export const getDefaultRoute = (user) => {
  if (!user) {
    return "/authentication/sign-in";
  }

  const menu = (user.menu || "").trim().toUpperCase();
  const dept = (user.bahagian || "").trim().toUpperCase();
  const userId = (user.id_pengguna || "").toLowerCase();

  // Admin users go to admin panel
  if (menu === "ADMIN" || userId === "admin") {
    return "/admin";
  }

  // Counter staff (KAUNTER) go to Dashboard
  if (menu === "KAUNTER") {
    return "/dashboard";
  }

  // Special department-specific routes
  if (dept === "UUU") {
    return "/notis/tunggakan";  // UUU department uses notis features
  }

  // Default: department staff go to dashboard
  return "/dashboard";
};

/**
 * Check if user can view specific department's data
 * 
 * @param {Object} user - User object
 * @param {string} targetDepartment - Department code to check access for
 * @returns {boolean} - True if user can view that department's data
 * 
 * @example
 * if (canViewDepartment(user, 'UEU')) {
 *   // Show UEU data
 * }
 */
export const canViewDepartment = (user, targetDepartment) => {
  if (!user || !targetDepartment) {
    return false;
  }

  const userDept = (user.bahagian || "").trim().toUpperCase();
  const targetDept = targetDepartment.trim().toUpperCase();

  // Users with CROSS_DEPARTMENT_VIEW permission can see all departments
  if (hasPermission(user, "CROSS_DEPARTMENT_VIEW")) {
    return true;
  }

  // Otherwise, can only view their own department
  return userDept === targetDept;
};

/**
 * Get display name for menu type
 * 
 * @param {string} menuType - Menu type code (ADMIN, KAUNTER, BAHAGIAN)
 * @returns {string} - User-friendly display name
 */
export const getMenuTypeName = (menuType) => {
  const names = {
    ADMIN: "Pentadbir Sistem",
    KAUNTER: "Kaunter",
    BAHAGIAN: "Bahagian",
  };

  return names[menuType?.toUpperCase()] || menuType || "Tidak Diketahui";
};

/**
 * Get all permissions for a user (for debugging/display)
 * 
 * @param {Object} user - User object
 * @returns {Array<string>} - Array of permission keys user has
 */
export const getUserPermissions = (user) => {
  if (!user) return [];

  return Object.keys(PERMISSIONS).filter((permKey) => hasPermission(user, permKey));
};
